import { useState, useCallback, useEffect } from 'react';

interface NavArrowState {
  isHovered: boolean;
  showLeftArrow: boolean;
  showRightArrow: boolean;
}

export const useNavArrows = (
  tabsContainerRef: React.RefObject<HTMLDivElement | null>
) => {
  const [navScrollArrows, setNavScrollArrows] = useState<NavArrowState>({
    isHovered: false, // Start with arrows hidden
    showLeftArrow: false,
    showRightArrow: false
  });

  const updateNavArrowVisibility = useCallback(() => {
    const container = tabsContainerRef.current;
    if (!container) return;
    
    const scrollLeft = container.scrollLeft;
    const maxScrollLeft = container.scrollWidth - container.offsetWidth;
    const offset = 1;

    const isAtLeftEdge = scrollLeft <= offset;
    const isAtRightEdge = Math.ceil(scrollLeft) >= maxScrollLeft - offset;

    setNavScrollArrows(prev => ({
      ...prev,
      showLeftArrow: !isAtLeftEdge && maxScrollLeft > 0,
      showRightArrow: !isAtRightEdge && maxScrollLeft > 0
    }));
  }, [tabsContainerRef]);

  const scrollNavLeft = useCallback(() => {
    const container = tabsContainerRef.current;
    if (!container) return;

    const scrollAmount = window.innerWidth / 3;
    const newScrollLeft = Math.max(0, container.scrollLeft - scrollAmount);

    container.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
    setTimeout(updateNavArrowVisibility, 300);
  }, [tabsContainerRef, updateNavArrowVisibility]);

  const scrollNavRight = useCallback(() => {
    const container = tabsContainerRef.current;
    if (!container) return;

    const scrollAmount = window.innerWidth / 3;
    const maxScroll = container.scrollWidth - container.offsetWidth;
    const newScrollLeft = Math.min(maxScroll, container.scrollLeft + scrollAmount);

    container.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
    setTimeout(updateNavArrowVisibility, 300);
  }, [tabsContainerRef, updateNavArrowVisibility]);

  const handleNavMouseEnter = useCallback(() => {
    setNavScrollArrows(prev => ({ ...prev, isHovered: true }));
    updateNavArrowVisibility();
  }, [updateNavArrowVisibility]);

  const handleNavMouseLeave = useCallback(() => {
    setNavScrollArrows(prev => ({
      ...prev,
      isHovered: false,
      showLeftArrow: false,
      showRightArrow: false
    }));
  }, []);

  // Check scroll position on mount and window resize
  useEffect(() => {
    const container = tabsContainerRef.current;
    if (!container) return;

    updateNavArrowVisibility();
    
    const onResize = () => {
      updateNavArrowVisibility();
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [tabsContainerRef, updateNavArrowVisibility]);

  // Scroll listener to update arrows while scrolling
  useEffect(() => {
    const container = tabsContainerRef.current;
    if (!container) return;

    const onScroll = () => {
      updateNavArrowVisibility();
    };

    container.addEventListener('scroll', onScroll, { passive: true });
    return () => container.removeEventListener('scroll', onScroll);
  }, [tabsContainerRef, updateNavArrowVisibility]);

  return {
    navScrollArrows,
    handleNavMouseEnter,
    handleNavMouseLeave,
    scrollNavLeft,
    scrollNavRight,
    updateNavArrowVisibility
  };
};
